import { ButtonProps, Button as VFZButton } from '@vfz/ui';
import React from 'react';
import { NavLink } from 'react-router-dom';

// Note: we omit theme and icons from ButtonProps because we use our own props to avoid conflict
export interface VZButtonProps extends Omit<ButtonProps, 'theme' | 'iconBefore' | 'iconAfter'> {
  /* to is navlink */
  to?: string;
  /* href is normal link */
  href?: string;
  /* onClick is button, also default */
  onClick?(e?: React.MouseEvent): void;
  name?: string;
  testid?: string;
  className?: string;
  external?: boolean;
  dotTheme?: 'primary' | 'secondary' | 'tertiary'; // if used in styled components
  iconBefore?: React.ReactNode;
  iconAfter?: React.ReactNode;
}

// Note: we intentionally avoid forwarding unrelated props to NavLink/anchor elements.

export const VZButton = ({
  children,
  disabled,
  type,
  to,
  href,
  name,
  testid,
  onClick,
  loading,
  external,
  size,
  dotTheme,
  variant,
  iconBefore,
  iconAfter,
  ...rest
}: VZButtonProps) => {
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!to && onClick) onClick();
  };

  if (to) {
    return (
      <VFZButton
        asChild
        name={name}
        type={type || 'button'}
        disabled={disabled}
        loading={loading}
        size={size}
        theme={dotTheme}
        variant={variant}
        {...rest}>
        <NavLink data-testid={testid} to={to} target={external ? '_blank' : '_self'}>
          {iconBefore}
          {children}
          {iconAfter}
        </NavLink>
      </VFZButton>
    );
  }

  if (href) {
    return (
      <VFZButton
        name={name}
        type={type || 'button'}
        onClick={handleClick}
        disabled={disabled}
        loading={loading}
        size={size}
        theme={dotTheme}
        variant={variant}
        asChild
        {...rest}>
        <a data-testid={testid} href={href} target={external ? '_blank' : '_self'} rel="noreferrer">
          {iconBefore}
          {children}
          {iconAfter}
        </a>
      </VFZButton>
    );
  }

  return (
    <VFZButton
      name={name}
      type={type || 'button'}
      data-testid={testid}
      onClick={handleClick}
      disabled={disabled}
      loading={loading}
      size={size}
      theme={dotTheme}
      variant={variant}
      {...rest}>
      {iconBefore}
      {children}
      {iconAfter}
    </VFZButton>
  );
};
